import React, { useState } from 'react';
import { 
  User, 
  Bell, 
  Database, 
  Palette, 
  Globe, 
  Shield, 
  Download,
  Key,
  Save,
  Check
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { ThemeDropdown } from '../ui/ThemeDropdown';
import { classNames } from '../../utils/classNames';

interface SettingsSection {
  id: string;
  name: string;
  icon: React.ElementType;
  description: string;
}

const settingsSections: SettingsSection[] = [
  {
    id: 'profile',
    name: 'Profile',
    icon: User,
    description: 'Personal information and preferences'
  },
  {
    id: 'notifications',
    name: 'Notifications',
    icon: Bell,
    description: 'Alert preferences and delivery methods'
  },
  {
    id: 'appearance',
    name: 'Appearance',
    icon: Palette,
    description: 'Theme and display preferences'
  },
  {
    id: 'data',
    name: 'Data & Privacy',
    icon: Database,
    description: 'Data management and privacy controls'
  },
  {
    id: 'integrations',
    name: 'Integrations',
    icon: Globe,
    description: 'External services and API connections'
  },
  {
    id: 'security',
    name: 'Security',
    icon: Shield,
    description: 'Account security and access controls'
  }
];

export const SettingsWorkflow: React.FC = () => {
  const [activeSection, setActiveSection] = useState('profile');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [saved, setSaved] = useState(false);
  const { theme } = useTheme();

  const handleSave = () => {
    // Simulate saving
    setSaved(true);
    setHasUnsavedChanges(false);
    setTimeout(() => setSaved(false), 2000);
  };

  const renderProfileSettings = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-white mb-4">Profile Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              First Name
            </label>
            <input
              type="text"
              placeholder="Enter your first name"
              className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              onChange={() => setHasUnsavedChanges(true)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Last Name
            </label>
            <input
              type="text"
              placeholder="Enter your last name"
              className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              onChange={() => setHasUnsavedChanges(true)}
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Email Address
            </label>
            <input
              type="email"
              placeholder="Enter your email"
              className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              onChange={() => setHasUnsavedChanges(true)}
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Organization
            </label>
            <input
              type="text"
              placeholder="Enter your organization"
              className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              onChange={() => setHasUnsavedChanges(true)}
            />
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-medium text-white mb-4">Role & Interests</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Primary Role
            </label>
            <select 
              className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              onChange={() => setHasUnsavedChanges(true)}
            >
              <option value="">Select your role</option>
              <option value="lobbyist">Lobbyist</option>
              <option value="policy-analyst">Policy Analyst</option>
              <option value="researcher">Researcher</option>
              <option value="government">Government Affairs</option>
              <option value="academic">Academic</option>
              <option value="journalist">Journalist</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Policy Areas of Interest
            </label>
            <div className="flex flex-wrap gap-2">
              {['Healthcare', 'Environment', 'Technology', 'Education', 'Defense', 'Finance', 'Energy', 'Transportation'].map((area) => (
                <button
                  key={area}
                  className="px-3 py-1 text-sm bg-slate-700/50 hover:bg-blue-600/20 border border-slate-600 hover:border-blue-500 rounded-full text-slate-300 hover:text-blue-300 transition-colors"
                  onClick={() => setHasUnsavedChanges(true)}
                >
                  {area}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderNotificationSettings = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-white mb-4">Email Notifications</h3>
        <div className="space-y-3">
          {[
            { id: 'analysis-complete', label: 'Analysis Reports Complete', description: 'When your policy analysis is ready' },
            { id: 'bill-updates', label: 'Bill Status Updates', description: 'Changes to tracked legislation' },
            { id: 'weekly-digest', label: 'Weekly Digest', description: 'Summary of legislative activity' },
            { id: 'urgent-alerts', label: 'Urgent Alerts', description: 'Time-sensitive policy developments' }
          ].map((notification) => (
            <div key={notification.id} className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg">
              <div>
                <div className="text-white font-medium">{notification.label}</div>
                <div className="text-sm text-slate-400">{notification.description}</div>
              </div>
              <input 
                type="checkbox" 
                className="h-4 w-4 text-blue-600 bg-slate-700 border-slate-600 rounded focus:ring-blue-500"
                onChange={() => setHasUnsavedChanges(true)}
              />
            </div>
          ))}
        </div>
      </div>

      <div>
        <h3 className="text-lg font-medium text-white mb-4">Delivery Preferences</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Digest Frequency
            </label>
            <select 
              className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white"
              onChange={() => setHasUnsavedChanges(true)}
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Delivery Time
            </label>
            <select 
              className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white"
              onChange={() => setHasUnsavedChanges(true)}
            >
              <option value="morning">Morning (8 AM)</option>
              <option value="afternoon">Afternoon (12 PM)</option>
              <option value="evening">Evening (6 PM)</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );

  const renderAppearanceSettings = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-white mb-4">Theme</h3>
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Select Theme
            </label>
            <ThemeDropdown />
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-medium text-white mb-4">Display Options</h3>
        <div className="space-y-3">
          {[
            { id: 'compact-mode', label: 'Compact Mode', description: 'Reduce spacing and padding throughout the interface' },
            { id: 'animations', label: 'Enable Animations', description: 'Smooth transitions and loading effects' },
            { id: 'sidebar-collapsed', label: 'Collapse Sidebar by Default', description: 'Start with sidebar in collapsed state' }
          ].map((option) => (
            <div key={option.id} className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg">
              <div>
                <div className="text-white font-medium">{option.label}</div>
                <div className="text-sm text-slate-400">{option.description}</div>
              </div>
              <input 
                type="checkbox" 
                className="h-4 w-4 text-blue-600 bg-slate-700 border-slate-600 rounded focus:ring-blue-500"
                onChange={() => setHasUnsavedChanges(true)}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderDataSettings = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-white mb-4">Data Export</h3>
        <div className="space-y-3">
          <Button className="flex items-center gap-2 bg-slate-700/50 hover:bg-slate-600/50">
            <Download className="w-4 h-4" />
            Export Analysis History
          </Button>
          <Button className="flex items-center gap-2 bg-slate-700/50 hover:bg-slate-600/50">
            <Download className="w-4 h-4" />
            Export Tracked Bills
          </Button>
          <Button className="flex items-center gap-2 bg-slate-700/50 hover:bg-slate-600/50">
            <Download className="w-4 h-4" />
            Export Profile Data
          </Button>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-medium text-white mb-4">Privacy Controls</h3>
        <div className="space-y-3">
          {[
            { id: 'analytics', label: 'Usage Analytics', description: 'Help improve Politheon by sharing anonymous usage data' },
            { id: 'personalization', label: 'Personalized Recommendations', description: 'Use your activity to suggest relevant content' },
            { id: 'third-party', label: 'Third-party Integrations', description: 'Allow approved third-party services to access your data' }
          ].map((control) => (
            <div key={control.id} className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg">
              <div>
                <div className="text-white font-medium">{control.label}</div>
                <div className="text-sm text-slate-400">{control.description}</div>
              </div>
              <input 
                type="checkbox" 
                className="h-4 w-4 text-blue-600 bg-slate-700 border-slate-600 rounded focus:ring-blue-500"
                onChange={() => setHasUnsavedChanges(true)}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderIntegrationsSettings = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-white mb-4">API Keys</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              OpenAI API Key
            </label>
            <div className="flex gap-2">
              <input
                type="password"
                placeholder="sk-..."
                className="flex-1 px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                onChange={() => setHasUnsavedChanges(true)}
              />
              <Button size="sm" className="bg-slate-700/50 hover:bg-slate-600/50">
                <Key className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Congress.gov API Key
            </label>
            <div className="flex gap-2">
              <input
                type="password"
                placeholder="Enter API key"
                className="flex-1 px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                onChange={() => setHasUnsavedChanges(true)}
              />
              <Button size="sm" className="bg-slate-700/50 hover:bg-slate-600/50">
                <Key className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-medium text-white mb-4">Connected Services</h3>
        <div className="space-y-3">
          {[
            { name: 'Slack', status: 'connected', description: 'Receive notifications in Slack channels' },
            { name: 'Microsoft Teams', status: 'disconnected', description: 'Share reports and collaborate with your team' },
            { name: 'Google Calendar', status: 'disconnected', description: 'Track committee hearings and legislative events' },
            { name: 'Salesforce', status: 'disconnected', description: 'Sync contacts and legislative tracking' }
          ].map((service) => (
            <div key={service.name} className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg">
              <div>
                <div className="text-white font-medium">{service.name}</div>
                <div className="text-sm text-slate-400">{service.description}</div>
              </div>
              <Button 
                size="sm" 
                variant={service.status === 'connected' ? 'secondary' : 'primary'}
                onClick={() => setHasUnsavedChanges(true)}
              >
                {service.status === 'connected' ? 'Disconnect' : 'Connect'}
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderSecuritySettings = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-white mb-4">Password & Authentication</h3>
        <div className="space-y-4">
          <Button className="bg-slate-700/50 hover:bg-slate-600/50">
            Change Password
          </Button>
          <div className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg">
            <div>
              <div className="text-white font-medium">Two-Factor Authentication</div>
              <div className="text-sm text-slate-400">Add an extra layer of security to your account</div>
            </div>
            <Button size="sm" variant="primary">
              Enable
            </Button>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-medium text-white mb-4">Session Management</h3>
        <div className="space-y-3">
          <Button className="bg-red-600/20 hover:bg-red-600/30 text-red-300 border border-red-600/30">
            Sign Out All Devices
          </Button>
          <div className="text-sm text-slate-400">
            Last sign-in: Today at 2:47 PM from Chrome on Windows
          </div>
        </div>
      </div>
    </div>
  );

  const renderSectionContent = () => {
    switch (activeSection) {
      case 'profile':
        return renderProfileSettings();
      case 'notifications':
        return renderNotificationSettings();
      case 'appearance':
        return renderAppearanceSettings();
      case 'data':
        return renderDataSettings();
      case 'integrations':
        return renderIntegrationsSettings();
      case 'security':
        return renderSecuritySettings();
      default:
        return null;
    }
  };

  return (
    <div className="h-full flex overflow-hidden">
      {/* Settings Navigation */}
      <div className="w-64 bg-slate-800/50 border-r border-slate-700/50 p-4">
        <h2 className="text-lg font-semibold text-white mb-4">Settings</h2>
        <nav className="space-y-1">
          {settingsSections.map((section) => {
            const Icon = section.icon;
            const isActive = activeSection === section.id;
            
            return (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={classNames(
                  'w-full flex items-start gap-3 p-3 rounded-lg text-left transition-colors',
                  isActive
                    ? 'bg-blue-600/20 text-blue-300 border border-blue-500/30'
                    : 'text-slate-300 hover:bg-slate-700/50 hover:text-white'
                )}
              >
                <Icon className={classNames(
                  'w-5 h-5 mt-0.5 flex-shrink-0',
                  isActive ? 'text-blue-400' : ''
                )} />
                <div className="min-w-0">
                  <div className="font-medium">{section.name}</div>
                  <div className="text-xs text-slate-400 mt-0.5">{section.description}</div>
                </div>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Settings Content */}
      <div className="flex-1 flex flex-col min-h-0">
        <div className="flex-1 overflow-y-auto p-6">
          <Card>
            <div className="p-6">
              {renderSectionContent()}
            </div>
          </Card>
        </div>

        {/* Save Bar */}
        {(hasUnsavedChanges || saved) && (
          <div className="border-t border-slate-700/50 bg-slate-800/80 backdrop-blur-sm p-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-slate-300">
                {saved ? 'Settings saved successfully!' : 'You have unsaved changes'}
              </div>
              <div className="flex gap-2">
                {!saved && (
                  <>
                    <Button 
                      variant="secondary" 
                      size="sm"
                      onClick={() => setHasUnsavedChanges(false)}
                    >
                      Cancel
                    </Button>
                    <Button 
                      size="sm" 
                      onClick={handleSave}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      Save Changes
                    </Button>
                  </>
                )}
                {saved && (
                  <div className="flex items-center gap-2 text-green-400">
                    <Check className="w-4 h-4" />
                    <span className="text-sm">Saved</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};